import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import SendIcon from '@mui/icons-material/Send';
import { Chip } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type { StatementQueueItem } from '@store/generated/billing';
import { useModal } from '@store/hooks';
import { format } from 'date-fns/format';

import { useStatementsQueue } from '../../../store';
import { BillingTable } from '../BillingTable';
import type { ColumnDefinition, TableAction } from '../types';

interface StatementsQueueProps {
  page: number;
  perPage: number;
  sort?: string;
  order: 'asc' | 'desc';
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onSort: (field: string) => void;
}

type QueueRow = StatementQueueItem & Record<string, unknown> & { id: string };

const formatCents = (cents: number | undefined | null): string => {
  if (cents == null) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  try {
    return format(new Date(dateString), 'MMM d, yyyy');
  } catch {
    return dateString;
  }
};

const TRIGGER_TYPE_LABELS: Record<string, string> = {
  ERA_RECEIPT: 'ERA Receipt',
};

const columns: ColumnDefinition<QueueRow>[] = [
  {
    field: 'patient_name',
    header: 'Patient',
    sortable: true,
    sticky: true,
    priority: 'high',
    width: 200,
  },
  {
    field: 'balance_cents',
    header: 'Balance',
    sortable: true,
    align: 'right',
    priority: 'high',
    width: 120,
    format: (value) => formatCents(value as number),
  },
  {
    field: 'encounter_count',
    header: 'Encounters',
    sortable: true,
    align: 'center',
    priority: 'medium',
    width: 110,
  },
  {
    field: 'created_at',
    header: 'Queued',
    sortable: true,
    priority: 'medium',
    width: 140,
    format: (value) => formatDate(value as string),
  },
  {
    field: 'trigger_type',
    header: 'Trigger',
    sortable: false,
    priority: 'low',
    mobileHidden: true,
    width: 140,
    format: (value) => (
      <Chip
        label={TRIGGER_TYPE_LABELS[value as string] ?? (value as string)}
        size="small"
        color="default"
        sx={{ textTransform: 'capitalize' }}
      />
    ),
  },
];

export const StatementsQueue: React.FC<StatementsQueueProps> = ({
  page,
  perPage,
  sort,
  order,
  onPageChange,
  onPerPageChange,
  onSort,
}) => {
  const { payment } = useEventTracking();
  const { open: openModal } = useModal();
  const { items, totalCount, isLoading } = useStatementsQueue({ page, perPage });
  const hasTrackedView = useRef(false);

  // Track queue viewed once data loads
  useEffect(() => {
    if (!isLoading && !hasTrackedView.current) {
      hasTrackedView.current = true;
      payment.statementsQueue.viewed({ total_count: totalCount });
    }
  }, [isLoading, totalCount, payment]);

  const handleSend = useCallback(
    (row: QueueRow) => {
      // Build reason from line items CPT codes
      const codes = row.line_items
        ?.map(li => li.billing_code)
        .filter(Boolean)
        .join(', ');
      const reason = codes
        ? `Patient responsibility — ${codes}`
        : 'Patient responsibility';

      // First encounter from line items (or null if multiple)
      const encounterIds = [...new Set(row.line_items?.map(li => li.encounter_id).filter(Boolean))];
      const encounterId = encounterIds.length === 1 ? encounterIds[0] : undefined;

      openModal(MODAL_TYPES.PAYMENT_REQUEST, {
        patientId: row.patient_id,
        patientName: row.patient_name,
        amount: (row.balance_cents / 100).toFixed(2),
        reason,
        encounterId,
        chargeLineIds: row.charge_line_ids,
        lineItems: row.line_items,
        source: 'statement_queue',
      });
    },
    [openModal],
  );

  // Wrap page change to track pagination
  const handlePageChange = (newPage: number) => {
    payment.statementsQueue.pageChanged({ page: newPage, per_page: perPage });
    onPageChange(newPage);
  };

  const handlePerPageChange = (newPerPage: number) => {
    payment.statementsQueue.pageChanged({ page: 1, per_page: newPerPage });
    onPerPageChange(newPerPage);
  };

  const rows = useMemo(
    () => items.map((item) => ({ ...item, id: item.patient_billing_account_id })),
    [items],
  );

  const actions: TableAction<QueueRow>[] = useMemo(
    () => [
      {
        label: () => 'Send Invoice',
        icon: () => <SendIcon sx={{ fontSize: 14 }} />,
        onClick: handleSend,
        color: 'primary' as const,
        variant: 'contained' as const,
        ariaLabel: (row: QueueRow) => `Send invoice to ${row.patient_name}`,
      },
    ],
    [handleSend],
  );

  return (
    <BillingTable
      data={rows}
      columns={columns}
      totalItems={totalCount}
      page={page}
      perPage={perPage}
      sortField={sort}
      sortOrder={order}
      onPageChange={handlePageChange}
      onPerPageChange={handlePerPageChange}
      onSort={onSort}
      loading={isLoading}
      selectable={false}
      actions={actions}
      emptyMessage="No patients ready for billing"
    />
  );
};
